// AssessmentExtras — renders the 7 Content Volume Picker extras inside the Study Guide.
// Honors `teacherMode`: when true, model answers / rubrics / exemplars are shown in both
// the on-screen view and the printed PDF. When false, students see the prompt only.
import { HelpCircle, Eye, EyeOff, ListChecks, PenLine, Brain, Scale, Compass, ClipboardCheck, Shuffle } from "lucide-react";
import { useState } from "react";

const SectionHeader = ({ icon: Icon, label, badge, count }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
    <div className="flex items-center gap-2 text-accent">
      <Icon className="h-4 w-4" />
      <h2 className="font-serif text-2xl font-semibold text-foreground">{label}</h2>
      {badge && <span className="rounded-full bg-accent/15 text-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">{badge}</span>}
    </div>
    {typeof count === "number" && (
      <span className="text-xs text-muted-foreground font-mono">{count} item{count === 1 ? "" : "s"}</span>
    )}
  </div>
);

// Collapsible row with on-screen Show/Hide + always-visible-in-print block (gated by teacherMode).
const KeyRow = ({ idx, teacherMode, kind, prompt, body, testIdPrefix }) => {
  const [open, setOpen] = useState(false);
  const showBody = teacherMode || open;
  return (
    <li data-testid={`${testIdPrefix}-${idx}`} className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
      <div className="px-5 py-4">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center h-7 w-7 rounded-lg font-serif font-bold text-sm shrink-0 text-primary" style={{ background: "hsl(var(--secondary))" }}>
            {idx + 1}
          </span>
          <div className="flex-1 min-w-0">
            {kind && <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent mb-1">{kind}</p>}
            <div className="text-sm sm:text-base font-medium text-foreground leading-relaxed">{prompt}</div>
          </div>
        </div>
        {body && !teacherMode && (
          <button
            data-testid={`${testIdPrefix}-toggle-${idx}`}
            onClick={() => setOpen((o) => !o)}
            className="no-print mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-accent hover:opacity-80 transition">
            {open ? <><EyeOff className="h-3.5 w-3.5" /> Hide model answer</> : <><Eye className="h-3.5 w-3.5" /> Show model answer</>}
          </button>
        )}
      </div>
      {body && showBody && (
        <div data-testid={`${testIdPrefix}-body-${idx}`} className="border-t border-accent/30 bg-accent/5 px-5 py-4">
          {body}
        </div>
      )}
      {/* Print: show body only when teacherMode is on, otherwise it's a student-blank page */}
      {body && teacherMode && !showBody && (
        <div className="hidden print:block border-t border-accent/30 bg-accent/5 px-5 py-4">{body}</div>
      )}
    </li>
  );
};

export const AssessmentExtras = ({ lecture, teacherMode = false }) => {
  const sa = lecture.short_answers || [];
  const fb = lecture.fill_blanks || [];
  const rc = lecture.recall_items || [];
  const dc = lecture.discussion_topics || [];
  const ct = lecture.critical_thinking || [];
  const le = lecture.legal_ethical || [];
  const ef = lecture.eye_to_future || [];
  const nothingToShow = ![sa, fb, rc, dc, ct, le, ef].some((x) => x.length);
  if (nothingToShow) return null;

  // Render a fill-in-the-blank with [[BLANK]] tokens as inline underlined gaps.
  const renderCloze = (sentence, answers, teacher) => {
    const parts = (sentence || "").split("[[BLANK]]");
    const spans = [];
    parts.forEach((p, i) => {
      spans.push(<span key={`t-${i}`}>{p}</span>);
      if (i < parts.length - 1) {
        spans.push(
          <span key={`b-${i}`} className={`inline-block mx-1 min-w-[90px] border-b-2 border-dotted border-muted-foreground/60 text-center font-mono text-sm ${teacher ? "text-accent font-bold" : "text-transparent"}`}>
            {teacher ? (answers[i] || "____") : "____"}
          </span>
        );
      }
    });
    return <p className="text-sm sm:text-base leading-loose">{spans}</p>;
  };

  return (
    <div className="space-y-10">
      {sa.length > 0 && (
        <section className="print-break" data-testid="extras-short-answers">
          <SectionHeader icon={PenLine} label="Short-Answer Questions" count={sa.length} badge="Instructor key" />
          <ul className="space-y-3">
            {sa.map((item, i) => (
              <KeyRow
                key={i} idx={i} teacherMode={teacherMode} testIdPrefix="sa"
                kind={item.max_points ? `${item.max_points} pts` : "Short answer"}
                prompt={item.question}
                body={
                  <div className="space-y-2">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent mb-1">Model answer</p>
                      <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">{item.model_answer}</p>
                    </div>
                    {item.rubric && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent mb-1">Rubric</p>
                        <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">{item.rubric}</p>
                      </div>
                    )}
                  </div>
                }
              />
            ))}
          </ul>
        </section>
      )}

      {fb.length > 0 && (
        <section className="print-break" data-testid="extras-fill-blanks">
          <SectionHeader icon={ListChecks} label="Fill in the Blanks" count={fb.length} badge={teacherMode ? "Answers filled" : "Blanks only"} />
          <ol className="space-y-3 list-decimal pl-5">
            {fb.map((item, i) => (
              <li key={i} data-testid={`fb-${i}`} className="rounded-xl border border-border bg-card px-5 py-4">
                {renderCloze(item.sentence, item.answers || [], teacherMode)}
                {item.distractors?.length > 0 && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    <span className="font-semibold">Word bank:</span> {[...(item.answers || []), ...item.distractors].sort().join(" · ")}
                  </p>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      {rc.length > 0 && (
        <section className="print-break" data-testid="extras-recall">
          <SectionHeader icon={Shuffle} label="Recall Questions" count={rc.length} />
          <ul className="space-y-2">
            {rc.map((item, i) => (
              <KeyRow key={i} idx={i} teacherMode={teacherMode} testIdPrefix="recall"
                prompt={item.question}
                body={
                  <>
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent mb-1">Answer</p>
                    <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">{item.answer}</p>
                  </>
                }
              />
            ))}
          </ul>
        </section>
      )}

      {dc.length > 0 && (
        <section className="print-break" data-testid="extras-discussion">
          <SectionHeader icon={HelpCircle} label="Topics for Discussion" count={dc.length} />
          <ul className="space-y-3">
            {dc.map((item, i) => (
              <KeyRow key={i} idx={i} teacherMode={teacherMode} testIdPrefix="disc"
                prompt={item.prompt}
                body={
                  <div className="space-y-2">
                    {item.model_response && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent mb-1">Strong-response guide</p>
                        <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">{item.model_response}</p>
                      </div>
                    )}
                    {item.perspectives?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent mb-1">Angles to raise</p>
                        <ul className="text-sm text-foreground/85 list-disc pl-5 space-y-0.5">
                          {item.perspectives.map((p, k) => <li key={k}>{p}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                }
              />
            ))}
          </ul>
        </section>
      )}

      {ct.length > 0 && (
        <section className="print-break" data-testid="extras-critical-thinking">
          <SectionHeader icon={Brain} label="Critical Thinking & Competency Practice" count={ct.length} />
          <ul className="space-y-3">
            {ct.map((item, i) => (
              <KeyRow key={i} idx={i} teacherMode={teacherMode} testIdPrefix="crit"
                kind={item.competencies?.length ? item.competencies.join(" · ") : "Analysis"}
                prompt={item.prompt}
                body={
                  <div className="space-y-2">
                    {item.model_response && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent mb-1">Exemplar response</p>
                        <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">{item.model_response}</p>
                      </div>
                    )}
                    {item.reasoning_steps?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent mb-1">Scaffolded steps</p>
                        <ol className="text-sm text-foreground/85 list-decimal pl-5 space-y-0.5">
                          {item.reasoning_steps.map((s, k) => <li key={k}>{s}</li>)}
                        </ol>
                      </div>
                    )}
                  </div>
                }
              />
            ))}
          </ul>
        </section>
      )}

      {le.length > 0 && (
        <section className="print-break" data-testid="extras-legal-ethical">
          <SectionHeader icon={Scale} label="Legal & Ethical Implications" count={le.length} />
          <ul className="space-y-3">
            {le.map((item, i) => (
              <li key={i} data-testid={`le-${i}`} className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
                <div className="px-5 py-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent mb-1">Scenario</p>
                  <p className="text-sm sm:text-base font-medium text-foreground leading-relaxed">{item.scenario}</p>
                </div>
                {teacherMode && (
                  <div className="border-t border-accent/30 bg-accent/5 px-5 py-4 space-y-3">
                    {item.legal_considerations && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent mb-1">Legal considerations</p>
                        <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">{item.legal_considerations}</p>
                      </div>
                    )}
                    {item.ethical_implications && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent mb-1">Ethical implications</p>
                        <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">{item.ethical_implications}</p>
                      </div>
                    )}
                  </div>
                )}
                {item.discussion_questions?.length > 0 && (
                  <div className="border-t border-border px-5 py-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-1">Discussion questions</p>
                    <ol className="text-sm text-foreground/85 list-decimal pl-5 space-y-0.5">
                      {item.discussion_questions.map((q, k) => <li key={k}>{q}</li>)}
                    </ol>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {ef.length > 0 && (
        <section className="print-break" data-testid="extras-eye-to-future">
          <SectionHeader icon={Compass} label="Eye to the Future" count={ef.length} />
          <ul className="space-y-3">
            {ef.map((item, i) => (
              <li key={i} data-testid={`eye-${i}`} className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
                <div className="px-5 py-4">
                  <p className="text-sm sm:text-base font-medium text-foreground leading-relaxed">{item.prompt}</p>
                  {item.scenario && (
                    <p className="mt-2 text-sm text-muted-foreground italic whitespace-pre-wrap">{item.scenario}</p>
                  )}
                </div>
                {item.exploration_questions?.length > 0 && (
                  <div className="border-t border-border px-5 py-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-1">Explore</p>
                    <ol className="text-sm text-foreground/85 list-decimal pl-5 space-y-0.5">
                      {item.exploration_questions.map((q, k) => <li key={k}>{q}</li>)}
                    </ol>
                  </div>
                )}
                {teacherMode && item.model_response && (
                  <div className="border-t border-accent/30 bg-accent/5 px-5 py-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent mb-1">Exemplar direction</p>
                    <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">{item.model_response}</p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default AssessmentExtras;
